/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  FETCH_SD_COC_APPLICATION_FORM,
  FETCH_SD_EXTERNALUSER_COC_STATUS,
  DELETE_APPLICATION_FORM_BY_APPLICATION_FORM_ID,
} from './actionTypes';

import {
  fetchSdCocApplicationFormUtil,
  fetchSdExternaluserCocStatusUtil,
  deleteApplicationFormByApplicationFormIdUtil,
} from './utils';

/**
 * Contains the action creator functions that update the UserCOC slice of the store.
 *
 * @namespace UserCOCActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_coc_application_form
   *
   * @param {string} changeType.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserCOCActions
   */
  static fetchSdCocApplicationForm = (dispatch, changeType, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdCocApplicationFormUtil.bind(null, changeType),

      FETCH_SD_COC_APPLICATION_FORM,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdCocApplicationForm.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserCOCActions
   */
  static resetFetchSdCocApplicationForm = (dispatch, data) => {
    const json = {
      type: FETCH_SD_COC_APPLICATION_FORM.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdCocApplicationForm.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserCOCActions
   */
  static resetFetchSdCocApplicationFormError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_COC_APPLICATION_FORM.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/sd_externaluser_coc_status
   *
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserCOCActions
   */
  static fetchSdExternaluserCocStatus = (dispatch, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      fetchSdExternaluserCocStatusUtil,
      FETCH_SD_EXTERNALUSER_COC_STATUS,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to fetchSdExternaluserCocStatus.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserCOCActions
   */
  static resetFetchSdExternaluserCocStatus = (dispatch, data) => {
    const json = {
      type: FETCH_SD_EXTERNALUSER_COC_STATUS.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to fetchSdExternaluserCocStatus.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserCOCActions
   */
  static resetFetchSdExternaluserCocStatusError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, FETCH_SD_EXTERNALUSER_COC_STATUS.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/ua/application_form/{application_form_id}
   *
   * @param {string} application_form_id.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof UserCOCActions
   */
  static deleteApplicationFormByApplicationFormId = (dispatch, application_form_id, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      deleteApplicationFormByApplicationFormIdUtil.bind(null, application_form_id),
      DELETE_APPLICATION_FORM_BY_APPLICATION_FORM_ID,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to deleteApplicationFormByApplicationFormId.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof UserCOCActions
   */
  static resetDeleteApplicationFormByApplicationFormId = (dispatch, data) => {
    const json = {
      type: DELETE_APPLICATION_FORM_BY_APPLICATION_FORM_ID.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to deleteApplicationFormByApplicationFormId.
   *
   * @param {function} dispatch
   * @function
   * @memberof UserCOCActions
   */
  static resetDeleteApplicationFormByApplicationFormIdError = dispatch => {
    ReduxUtils.resetErrorAction(
      dispatch,
      DELETE_APPLICATION_FORM_BY_APPLICATION_FORM_ID.failureAction
    );
  };
}
